import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";

const title = "Grade Levels, Ages 3–18 | Swedish International School";
const description =
  "Preschool, preschool class and primary school Years 1–6 on site, plus lower secondary via Sofia Distans and sixth form via Hermods with supervision in Estepona.";

export const Route = createFileRoute("/en/grade-levels")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/grade-levels") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/grade-levels"),
  }),
  component: GradeLevels,
});

const levels = [
  {
    name: "Preschool",
    age: "3–5 years",
    text: "A playful start with Swedish as the main language and an early introduction to Spanish and English.",
  },
  {
    name: "Preschool class",
    age: "5–6 years",
    text: "The transition to school, focusing on language, social interaction and a sense of security in the group.",
  },
  {
    name: "Primary school",
    age: "Years 1–6",
    text: "Swedish curriculum with qualified teachers, written assessments and extended Spanish on the timetable.",
  },
  {
    name: "Lower secondary",
    age: "Years 7–9",
    text: "Studies via Sofia Distans with on-site supervisors at the school's premises.",
  },
  {
    name: "Sixth form",
    age: "Years 1–3",
    text: "Studies via Hermods with supervision from us, combined with a sports profile if you wish.",
  },
  {
    name: "After-school care",
    age: "After school",
    text: "Leisure activities alongside the school, with movement and outdoor activities.",
  },
];

function GradeLevels() {
  return (
    <>
      <PageHero
        eyebrow="Grade levels"
        title="From preschool to sixth-form studies"
        intro="Swedish International School welcomes pupils from preschool to Year 6 on site, as well as lower and upper secondary pupils by distance learning with supervision. The aim is teaching in Swedish with extended Spanish and English."
      />

      <section className="mx-auto max-w-6xl px-5 py-16">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {levels.map((l) => (
            <article key={l.name} className="rounded-xl border border-border bg-card p-7 shadow-soft">
              <p className="text-xs font-semibold tracking-[0.18em] text-primary uppercase">{l.age}</p>
              <h2 className="mt-3 text-xl font-semibold">{l.name}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{l.text}</p>
            </article>
          ))}
        </div>
      </section>

      <Prose>
        <h2>How the stages fit together</h2>
        <p>
          The school follows the Swedish national curriculum and works in age-based groups. Preschool
          focuses on play, language and a sense of security, the preschool class prepares children for
          school work, and from Year 1 teaching follows the core content of each subject syllabus.
          Because the school is small, teachers work closely together and know every pupil, which
          makes it easier to adapt pace and content to the individual.
        </p>
        <p>
          From lower secondary, pupils study via Sofia Distans and at sixth-form level via Hermods.
          Pupils are on site at the school during the school day and have supervisors who help with
          planning, study technique and follow-up. Grades and course certificates are issued by the
          respective distance school.
        </p>

        <h2>Languages throughout school</h2>
        <p>
          The language of instruction is Swedish. Spanish is on the timetable from preschool and
          continues throughout primary school, and English is introduced early and built on in line
          with the curriculum. Many of our pupils live multilingual everyday lives, and the aim is for
          them to switch languages with confidence — in the classroom, in the playground and outside
          school.
        </p>

        <h2>Transitions between stages</h2>
        <p>
          Transitions are planned together with guardians. Before a child starts with us we review
          previous schooling, any support needs and where the pupil is in the syllabus, so the start
          is as smooth as possible. Starting mid-term is fine; contact us and we'll check availability
          in the relevant year group.
        </p>
        <p>
          If you are unsure which year group applies, we use the pupil's year of birth according to
          the Swedish model, but always make an individual assessment together with you. The{" "}
          <a className="text-primary link-quiet" href="/en/application">application form</a>{" "}
          suggests a year group automatically based on date of birth, and you can change it.
        </p>

        <h2>What the school day includes</h2>
        <p>
          The school day includes lunch at school, outdoor time and movement. After school there is{" "}
          <a className="text-primary link-quiet" href="/en/leisure-activities">after-school care</a>,
          and optional sports profiles in padel and tennis as well as the football academy. Fees and
          what is included at each stage are listed on the{" "}
          <a className="text-primary link-quiet" href="/en/fees">fees page</a>.
        </p>
      </Prose>

      <FaqBlock items={faqFor("arskurser", "en")} locale={"en"} />

      <CtaBand
        title="Not sure which grade level is right?"
        text="Contact us and we'll go through the options together with your family."
      />
      <div className="h-20" />
    </>
  );
}
